import React, { useState } from 'react';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Alert,
  Box,
  Checkbox,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { HDialog, HDialogContent } from 'BaseComponents';
import { UNSYNC_REASONS } from 'utils/constants/question.constant';
import { useUnsyncParametersFromMaster } from 'hooks/questionAPIs';


export const UnsyncParameterDialog = ({
  open, row, onClose, onSuccess,
}) => {
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [search, setSearch] = useState('');

  const { mutateAsync: unsyncParameters, isLoading } = useUnsyncParametersFromMaster();

  // Create a lookup map for selected question ids to unsync
  const selectedIdsMap = React.useMemo(() => (
    selectedQuestionIds.reduce((acc, id) => ({
      ...acc,
      [id]: true,
    }), {})
  ), [selectedQuestionIds]);

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedQuestionIds([]);
      setSearch('');
    }
  }, [open]);

  const handleToggle = (qID) => {
    setSelectedQuestionIds((prev) => (prev.includes(qID)
      ? prev.filter((id) => id !== qID)
      : [...prev, qID]));
  };

  const handleSubmit = () => {
    // Collect Parameter IDs for the selected questions
    const parameterIDs = selectedQuestionIds
      .map((qID) => row.questions.find((q) => q.ID === qID)?.parameterID)
      .filter(Boolean);

    if (parameterIDs.length === 0) return;

    // Send Unsync Request for all selected Parameter IDs
    unsyncParameters(
      {
        parameterIDs,
        reason: UNSYNC_REASONS.USER_MANUALLY_UNSYNCED_TAXONOMY,
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      },
    );
  };

  // Table Interactions below

  // Filter questions based on search
  const filteredQuestions = React.useMemo(() => {
    if (!row?.questions) return [];
    if (!search.trim()) return row.questions;
    return row.questions.filter((q) => (
      q.title.toLowerCase().includes(search.toLowerCase(),
      )),
    );
  }, [row?.questions, search]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Select all currently visible (filtered) questions
      const visibleIds = filteredQuestions.map((q) => q.ID);
      // Merge with existing selection (ensure uniqueness [no duplicate IDs])
      setSelectedQuestionIds((prev) => [...new Set([...prev, ...visibleIds])]);
    }
    else {
      // Deselect all currently visible (filtered) questions
      const visibleIds = filteredQuestions.map((q) => q.ID);
      setSelectedQuestionIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    }
  };

  // Determine Select All state
  const visibleCount = filteredQuestions.length;

  const { isAllSelected, isIndeterminate } = React.useMemo(() => {
    const selectedVisibleCount = filteredQuestions.filter((q) => (
      selectedQuestionIds.includes(q.ID)
    )).length;

    return {
      isAllSelected: visibleCount > 0 && selectedVisibleCount === visibleCount,
      isIndeterminate: selectedVisibleCount > 0 && selectedVisibleCount < visibleCount,
    };
  }, [filteredQuestions, selectedQuestionIds, visibleCount]);

  return (
    <HDialog
      open={open}
      onClose={onClose}
      title='Stop Syncing Parameter'
      onSubmit={handleSubmit}
      submitText={`Stop Syncing (${selectedQuestionIds.length})`}
      loading={isLoading}
      maxWidth='md'
      fullWidth
      disabled={selectedQuestionIds.length === 0}
    >
      <HDialogContent>
        <Typography variant='body1'>
          Select the questions where you want to stop syncing the parameter
          {' '}
          <strong>{row?.name}</strong>
          :
        </Typography>

        <Box sx={{ my: 2 }}>
          <TextField
            size='small'
            autoComplete='off'
            placeholder='Search questions...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchRoundedIcon fontSize='medium' />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer
          sx={{
            maxHeight: 300,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            mb: 2,
          }}
        >
          <Table stickyHeader size='small'>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox'>
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                    disabled={visibleCount === 0}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2' fontWeight={600}>
                    Question Title
                    {visibleCount !== row?.questions?.length && (
                      ` (Showing ${visibleCount} of ${row?.questions?.length})`
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleCount > 0 ? (
                filteredQuestions.map((q) => {
                  const isSelected = !!selectedIdsMap[q.ID];
                  return (
                    <TableRow
                      key={q.ID}
                      hover
                      onClick={() => handleToggle(q.ID)}
                      role='checkbox'
                      aria-checked={isSelected}
                      selected={isSelected}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding='checkbox'>
                        <Checkbox checked={isSelected} />
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>{q.title}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align='center' sx={{ py: 3 }}>
                    <Typography variant='body2' color='textSecondary'>
                      {search ? 'No questions match your search.' : 'No questions found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Alert severity='warning' sx={{ mb: 2 }}>
          <Typography>
            These parameters will become independent.
            {' '}
            Future updates to the synced parameter will
            {' '}
            <strong>not</strong>
            {' '}
            apply to them.
          </Typography>
        </Alert>
      </HDialogContent>
    </HDialog>
  );
};
